import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo } from 'react';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Info, Loader2, Search } from 'lucide-react';
import { compliance, adminUsers, type AuditFilters, getAuthToken } from '@/api/client';
import { humanAction, humanRole, humanResourceType, humanDetails } from '@/lib/humanise';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


async function downloadCsv(url: string, filename: string) {
  const token = getAuthToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

type SearchFilters = Omit<AuditFilters, 'page' | 'limit'>;
type SortKey = 'timestamp' | 'action' | 'user' | 'role' | 'resource';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [25, 50, 100] as const;

// Human-friendly action labels for the multi-select dropdown.
const ACTION_OPTIONS: MultiSelectOption[] = [
  { code: 'auth.login.password.success', label: 'Password verified' },
  { code: 'auth.login.password.failure', label: 'Password failed' },
  { code: 'auth.login.complete', label: 'Logged in' },
  { code: 'auth.logout', label: 'Logged out' },
  { code: 'auth.token.refreshed', label: 'Session refreshed' },
  { code: 'auth.site_switched', label: 'Switched site' },
  { code: 'auth.mfa.enrolled', label: 'MFA enrolled' },
  { code: 'auth.mfa.bypassed', label: 'MFA bypassed (testing)' },
  { code: 'auth.password_reset.requested', label: 'Password reset requested' },
  { code: 'auth.password_reset.completed', label: 'Password reset completed' },
  { code: 'auth.password.locked', label: 'Account locked' },
  { code: 'auth.unlocked', label: 'Account unlocked' },
  { code: 'site.created', label: 'Site created' },
  { code: 'site.updated', label: 'Site updated' },
  { code: 'site.deleted', label: 'Site deleted' },
  { code: 'invite.sent', label: 'Invitation sent' },
  { code: 'invite.resent', label: 'Invitation resent' },
  { code: 'invite.activated', label: 'Invitation accepted' },
  { code: 'admin.user.created', label: 'User created' },
  { code: 'admin.user.deleted', label: 'User deleted' },
  { code: 'admin.user.locked', label: 'User locked' },
  { code: 'admin.user.unlocked', label: 'User unlocked' },
  { code: 'admin.user.role_changed', label: 'Role changed' },
  { code: 'subject.created', label: 'Subject created' },
  { code: 'subject.updated', label: 'Subject updated' },
  { code: 'phi.grant.created', label: 'PHI access granted' },
  { code: 'phi.grant.revoked', label: 'PHI access revoked' },
  { code: 'csr.job.created', label: 'CSR export requested' },
  { code: 'csr.job.ready', label: 'CSR export ready' },
  { code: 'csr.job.downloaded', label: 'CSR export downloaded' },
  { code: 'compliance.audit_trail.exported', label: 'Audit trail exported' },
  { code: 'compliance.access_log.exported', label: 'Access log exported' },
].map((a) => ({ value: a.code, label: a.label, sublabel: a.code }));

function localToIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}
function isoToLocal(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AuditTrailTab() {
  // Client rule, 2026-10-09: an IP address is masked for everyone except
  // site admins and the platform tier. The server already nulls the value
  // for those callers — this hides the column too, so nobody stares at a
  // row of dashes wondering whether the data is missing or withheld.
  const showIp = useMyPermissions().holdsOperation('compliance.view_identifiers');

  const { user } = useAuth();
  const [filters, setFilters] = useState<SearchFilters>({});
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [fromLocal, setFromLocal] = useState('');
  const [toLocal, setToLocal] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });
  const usersById = useMemo(() => {
    const m = new Map<string, { full_name: string; email: string }>();
    users.data?.rows.forEach((u) => m.set(u.user_id, { full_name: u.full_name, email: u.email }));
    return m;
  }, [users.data]);
  const userOptions = useMemo<MultiSelectOption[]>(
    () =>
      (users.data?.rows ?? []).map((u) => ({
        value: u.user_id,
        label: u.full_name || u.email.split('@')[0] || u.email,
        sublabel: u.email,
      })),
    [users.data],
  );

  // Site-name lookup is what makes `switched_site` render the actual site
  // names instead of UUID slices. Compliance users aren't necessarily platform
  // admins, so we source from the auth context (siteNames) rather than
  // adminSites.list (which is 403 for non-platform users).
  const sitesById = useMemo(() => {
    const m = new Map<string, string>();
    if (user?.siteNames) {
      for (const [id, name] of Object.entries(user.siteNames)) m.set(id, name);
    }
    return m;
  }, [user?.siteNames]);

  const queryFilters: AuditFilters = { ...filters, page, limit: pageSize };

  const q = useQuery({
    queryKey: ['audit-trail', queryFilters],
    queryFn: () => compliance.listAuditTrail(queryFilters),
  });

  function applyFilters() {
    setFilters({
      user_id: selectedUserIds.length > 0 ? selectedUserIds : undefined,
      action: selectedActions.length > 0 ? selectedActions : undefined,
      from: localToIso(fromLocal),
      to: localToIso(toLocal),
    });
    setPage(1);
  }

  function clearFilters() {
    setSelectedUserIds([]); setSelectedActions([]); setFromLocal(''); setToLocal('');
    setFilters({}); setPage(1);
  }

  function handlePageSizeChange(size: number) { setPageSize(size); setPage(1); }

  async function handleExport() {
    setExportingCsv(true);
    try {
      const url = compliance.exportAuditTrailUrl(filters);
      const today = new Date().toISOString().slice(0, 10);
      await downloadCsv(url, `audit-trail-${today}.csv`);
      toast.success('Audit trail exported');
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setExportingCsv(false);
    }
  }

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir(k === 'timestamp' ? 'desc' : 'asc'); }
  }
  function sortIndicator(k: SortKey) {
    if (sortKey !== k) return null;
    return <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  const sortedRows = useMemo(() => {
    const rows = q.data?.rows ?? [];
    const cmp = (a: typeof rows[number], b: typeof rows[number]): number => {
      let v = 0;
      switch (sortKey) {
        case 'timestamp':
          v = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          break;
        case 'action':
          v = humanAction(a.action).localeCompare(humanAction(b.action));
          break;
        case 'user': {
          const an = usersById.get(a.user_id ?? '');
          const bn = usersById.get(b.user_id ?? '');
          v = (an?.full_name || an?.email || a.user_id || '').localeCompare(bn?.full_name || bn?.email || b.user_id || '');
          break;
        }
        case 'role':
          v = humanRole(a.user_role_at_time).localeCompare(humanRole(b.user_role_at_time));
          break;
        case 'resource':
          v = (a.resource_type || '').localeCompare(b.resource_type || '');
          break;
      }
      return sortDir === 'asc' ? v : -v;
    };
    return [...rows].sort(cmp);
  }, [q.data, sortKey, sortDir, usersById]);

  const hasMore = q.data?.hasMore ?? false;
  const total = q.data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : page;

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="label text-xs" htmlFor="f-user">User</label>
            <MultiSelectSearch
              id="f-user"
              options={userOptions}
              value={selectedUserIds}
              onChange={setSelectedUserIds}
              placeholder="Any user"
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="f-action">Action</label>
            <MultiSelectSearch
              id="f-action"
              options={ACTION_OPTIONS}
              value={selectedActions}
              onChange={setSelectedActions}
              placeholder="Any action"
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="f-from">From</label>
            <input
              id="f-from"
              type="datetime-local"
              className="input"
              value={fromLocal || isoToLocal(filters.from)}
              onChange={(e) => setFromLocal(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs" htmlFor="f-to">To</label>
            <input
              id="f-to"
              type="datetime-local"
              className="input"
              value={toLocal || isoToLocal(filters.to)}
              onChange={(e) => setToLocal(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={applyFilters} className="btn-secondary">
            <Search className="h-4 w-4" /> Search
          </button>
          <button type="button" onClick={clearFilters} className="btn-ghost text-xs">
            Clear
          </button>
          <button type="button" onClick={handleExport}
            disabled={exportingCsv || !q.data?.rows?.length} className="btn-secondary">
            {exportingCsv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-ink-muted" htmlFor="at-page-size">Rows per page:</label>
            <select
              id="at-page-size"
              className="input py-1 text-xs w-20"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {q.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {q.isError && (
          <div className={`flex items-start gap-2 border-b p-4 text-sm ${errorToneClass(q.error)}`}>
            {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(q.error, 'Failed to load audit trail.')}</span>
          </div>
        )}
        {!q.isLoading && q.data && q.data.rows.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">No audit entries match the filters.</div>
        )}
        {q.data && q.data.rows.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                  <tr>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('timestamp')}>
                      <span className="inline-flex items-center gap-1">Timestamp {sortIndicator('timestamp')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('action')}>
                      <span className="inline-flex items-center gap-1">Action {sortIndicator('action')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('user')}>
                      <span className="inline-flex items-center gap-1">User {sortIndicator('user')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('role')}>
                      <span className="inline-flex items-center gap-1">Role {sortIndicator('role')}</span>
                    </th>
                    <th className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort('resource')}>
                      <span className="inline-flex items-center gap-1">Resource {sortIndicator('resource')}</span>
                    </th>
                    {showIp && <th className="px-3 py-2">IP</th>}
                    <th className="px-3 py-2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => {
                    const u = r.user_id ? usersById.get(r.user_id) : null;
                    const detailsText = humanDetails(
                      r.action,
                      r.new_value as unknown as Record<string, unknown> | null,
                      { users: usersById, sites: sitesById },
                    );
                    return (
                      <tr key={r.audit_id} className="border-b border-surface-border last:border-0 hover:bg-surface-muted/40 align-top">
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-secondary">
                          {fmtDateTime(r.timestamp)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="badge badge-info">{humanAction(r.action)}</span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {u ? (
                            <>
                              <div className="font-medium text-ink-primary">{u.full_name || u.email.split('@')[0]}</div>
                              <div className="text-[10px] text-ink-muted">{u.email}</div>
                            </>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{humanRole(r.user_role_at_time)}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.resource_type ? humanResourceType(r.resource_type) : '—'}
                        </td>
                        {showIp && (
                          <td className="px-3 py-2 font-mono text-xs text-ink-muted">{r.ip ?? '—'}</td>
                        )}
                        <td className="px-3 py-2 text-xs max-w-md">
                          {detailsText ? (
                            <div className="text-ink-secondary">{detailsText}</div>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-surface-border px-4 py-3">
              <span className="text-xs text-ink-muted">
                Page {page} of {totalPages} &middot; {total.toLocaleString()} total records
                <span className="ml-2 italic">(sort applies to this page only)</span>
              </span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setPage(1)} disabled={page <= 1}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="First page">
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Previous page">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs font-medium text-ink-secondary">{page}</span>
                <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!hasMore}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Next page">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages || total === 0}
                  className="btn-secondary py-1 px-2 text-xs disabled:opacity-40" title="Last page">
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
